import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { adminApi } from './admin';
import {
  MAX_PAGE_SIZE,
  PAGE_SIZE,
  type AdminCollection,
  type AdminCreator,
  type AdminProgramme,
  type AdminVideo,
  type ListFilters,
  type Page,
  type ProgrammeListItem,
  type ProgrammeListParams,
  type TaxonomyTerm,
  type TermListParams,
  type ListParams,
  type VideoListParams,
  type Vocabulary,
} from './types';

/**
 * Query keys. Every list lives under its entity prefix (`['programmes', …]`), so invalidating
 * the prefix refreshes the paged lists, the pickers and (for taxonomy) the fetch-all caches.
 */
export const qk = {
  me: ['me'] as const,
  staff: ['staff'] as const,
  creators: (p?: object) => ['creators', 'list', p ?? {}] as const,
  creator: (id: string) => ['creator', id] as const,
  programmes: (p?: object) => ['programmes', 'list', p ?? {}] as const,
  programme: (id: string) => ['programme', id] as const,
  /** Every term of a vocabulary (all pages). */
  taxonomy: (vocabulary?: Vocabulary, includeArchived = false) =>
    ['taxonomy', 'all', vocabulary ?? 'all', includeArchived] as const,
  terms: (p?: object) => ['taxonomy', 'list', p ?? {}] as const,
  /** Under the taxonomy prefix: renaming or deleting a group changes its terms too. */
  formatGroups: ['taxonomy', 'format-groups'] as const,
  collections: (p?: object) => ['collections', 'list', p ?? {}] as const,
  collection: (id: string) => ['collection', id] as const,
  homeLayouts: ['home-layouts'] as const,
  videos: (p?: object) => ['videos', 'list', p ?? {}] as const,
  video: (id: string) => ['video', id] as const,
};

/** A paged list flattened for display. */
export interface PagedList<T> {
  items: T[];
  /** Rows matching the filters, across all pages. */
  total: number;
}

/** Drop empty filters so `{q: ''}` and `{}` share a cache entry (and a cursor chain). */
function clean<P extends object>(params: P): P {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  ) as P;
}

/**
 * A server-paged list: `PAGE_SIZE` rows per page, "Load more" follows `next_cursor`. The filters
 * are the query key, so changing `q` or a filter starts again from the first page (a cursor
 * only belongs to the filter set that produced it). While the next filter set loads, the
 * previous rows stay on screen (`isPlaceholderData`).
 */
function usePagedList<T, P extends ListParams>(
  key: readonly unknown[],
  fetchPage: (params: P) => Promise<Page<T>>,
  filters: P,
  options: { enabled?: boolean } = {},
) {
  return useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) => fetchPage({ ...filters, limit: PAGE_SIZE, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled: options.enabled,
    select: (data: InfiniteData<Page<T>, string | undefined>): PagedList<T> => ({
      items: data.pages.flatMap((p) => p.items),
      total: data.pages[data.pages.length - 1]?.total ?? 0,
    }),
  });
}

export type PagedListQuery<T> = ReturnType<typeof usePagedList<T, ListParams>>;

type ListOptions = { enabled?: boolean };

export function useProgrammeList(
  params: ListFilters<ProgrammeListParams>,
  options?: ListOptions,
): PagedListQuery<ProgrammeListItem> {
  const filters = clean(params);
  return usePagedList(qk.programmes(filters), adminApi.listProgrammes, filters, options);
}

export function useVideoList(
  params: ListFilters<VideoListParams>,
  options?: ListOptions,
): PagedListQuery<AdminVideo> {
  const filters = clean(params);
  return usePagedList(qk.videos(filters), adminApi.listVideos, filters, options);
}

export function useCreatorList(
  params: ListFilters<ListParams>,
  options?: ListOptions,
): PagedListQuery<AdminCreator> {
  const filters = clean(params);
  return usePagedList(qk.creators(filters), adminApi.listCreators, filters, options);
}

export function useCollectionList(
  params: ListFilters<ListParams>,
  options?: ListOptions,
): PagedListQuery<AdminCollection> {
  const filters = clean(params);
  return usePagedList(qk.collections(filters), adminApi.listCollections, filters, options);
}

export function useTermList(
  params: ListFilters<TermListParams>,
  options?: ListOptions,
): PagedListQuery<TaxonomyTerm> {
  const filters = clean(params);
  return usePagedList(qk.terms(filters), adminApi.listTaxonomy, filters, options);
}

export function useProgramme(id: string) {
  return useQuery({ queryKey: qk.programme(id), queryFn: () => adminApi.getProgramme(id) });
}

/** Store a fresh AdminProgramme (from a mutation response) and refresh list views. */
// Programmes this tab just changed outside the editor form (episodes, video, workflow). The editor
// adopts the resulting revision bump instead of treating it as someone else's edit.
const ownProgrammeWrites = new Set<string>();

export function markOwnProgrammeWrite(programmeId: string) {
  ownProgrammeWrites.add(programmeId);
}

export function takeOwnProgrammeWrite(programmeId: string): boolean {
  return ownProgrammeWrites.delete(programmeId);
}

export function useSetProgramme() {
  const qc = useQueryClient();
  return (p: AdminProgramme) => {
    qc.setQueryData(qk.programme(p.id), p);
    void qc.invalidateQueries({ queryKey: ['programmes'] });
  };
}

/** One creator, e.g. for the label of a selected id that isn't in the loaded options. */
export function useCreator(id: string | null | undefined) {
  return useQuery({
    queryKey: qk.creator(id ?? ''),
    queryFn: () => adminApi.getCreator(id!),
    enabled: !!id,
    staleTime: 5 * 60_000,
  });
}

/** Fetch every page of a list, following `next_cursor` until it is null. */
export async function fetchAllPages<T>(
  fetchPage: (cursor: string | undefined) => Promise<Page<T>>,
): Promise<T[]> {
  const items: T[] = [];
  let cursor: string | undefined;
  do {
    const page = await fetchPage(cursor);
    items.push(...page.items);
    cursor = page.next_cursor ?? undefined;
  } while (cursor);
  return items;
}

/**
 * Every term of one vocabulary, for the classification pickers and interest→subject mapping.
 * Vocabularies are bounded but can pass one page, so this follows the cursor to the end; one
 * cached query per vocabulary.
 */
export function useTaxonomy(vocabulary: Vocabulary, includeArchived = false) {
  return useQuery({
    queryKey: qk.taxonomy(vocabulary, includeArchived),
    queryFn: () =>
      fetchAllPages((cursor) =>
        adminApi.listTaxonomy({
          vocabulary,
          archived: includeArchived,
          limit: MAX_PAGE_SIZE,
          cursor,
        }),
      ),
    staleTime: 5 * 60_000,
  });
}

/** Every format group, in display order (a short, unpaged list). */
export function useFormatGroups() {
  return useQuery({
    queryKey: qk.formatGroups,
    queryFn: adminApi.listFormatGroups,
    staleTime: 5 * 60_000,
  });
}

const TERMINAL = new Set(['ready', 'errored']);

/** How often a non-terminal video is re-fetched (GET also re-checks Bunny). Tests shorten it. */
export const videoPolling = { ms: 5000 };

/** A single video, polled until it is ready or errored. */
export function useVideo(id: string | null | undefined) {
  return useQuery({
    queryKey: qk.video(id ?? ''),
    queryFn: () => adminApi.getVideo(id!),
    enabled: !!id,
    refetchInterval: (query) =>
      query.state.data && TERMINAL.has(query.state.data.status) ? false : videoPolling.ms,
  });
}
